// WhatsApp sessions via WasenderAPI (proxied by the backend at /api/whatsapp).

import type { PaginatedResponse } from '@/lib/api-client'

export type WhatsappStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'need_scan'
  | 'logged_out'
  | 'expired'

export interface WhatsappSession {
  id: number
  name: string
  phone_number: string
  status: WhatsappStatus
  account_protection: boolean
  log_messages: boolean
  created_at: string
  updated_at: string
}

export interface CreateSessionInput {
  name: string
  phoneNumber: string
}

export interface ConnectResult {
  status: string
  qrCode?: string
}

// --- Messages (persisted audit log) --------------------------------------

// `guardian` is the API's legacy identifier for a Contact recipient.
export type WhatsappRecipientType = 'student' | 'guardian' | 'teacher'
export type WhatsappMessageStatus =
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'

export interface WhatsappMessage {
  id: string
  recipientType: WhatsappRecipientType
  recipientId: string | null
  recipientName: string
  recipientKind?: 'person' | 'company'
  recipientGender: 'male' | 'female' | null
  recipientDateOfBirth: string | null
  toPhone: string
  body: string
  sessionId: string | null
  sessionName: string | null
  status: WhatsappMessageStatus
  providerId: string | null
  error: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface SendMessageInput {
  recipientType: WhatsappRecipientType
  recipientId?: string | null
  recipientName: string
  toPhone: string
  body: string
}

export interface ListMessagesParams {
  recipientType?: WhatsappRecipientType
  recipientId?: string
  limit?: number
  offset?: number
}

export interface CheckNumberResult {
  exists: boolean
}

export type WhatsappMessageListResponse = PaginatedResponse<WhatsappMessage>

export const RECIPIENT_TYPE_LABELS: Record<WhatsappRecipientType, string> = {
  student: 'Estudiante',
  guardian: 'Contacto',
  teacher: 'Profesor',
}

export const MESSAGE_STATUS_LABELS: Record<WhatsappMessageStatus, string> = {
  pending: 'Enviando',
  sent: 'Enviado',
  delivered: 'Entregado',
  read: 'Leído',
  failed: 'Falló',
}

export const MESSAGE_STATUS_TONE: Record<WhatsappMessageStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-600',
  sent: 'bg-muted text-muted-foreground',
  delivered: 'bg-sky-500/10 text-sky-600',
  read: 'bg-emerald-500/10 text-emerald-600',
  failed: 'bg-rose-500/10 text-rose-600',
}
